<template>
  <div class="email-admin-page">
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-header d-flex align-items-center justify-content-between">
            <h4 class="card-title mb-0">
              <i class="bi bi-envelope me-2"></i>Email Management
            </h4>
          </div>
          <div class="card-body">
            <!-- Tabs Navigation -->
            <ul class="nav nav-tabs mb-4">
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'automated' }" @click="setActiveTab('automated')" href="#">
                  <i class="bi bi-file-earmark-text me-2"></i>Automated Templates
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'custom' }" @click="setActiveTab('custom')" href="#">
                  <i class="bi bi-file-earmark-plus me-2"></i>Custom Templates
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'send' }" @click="setActiveTab('send')" href="#">
                  <i class="bi bi-send me-2"></i>Send Email
                </a>
              </li>
              <li class="nav-item">
                <a class="nav-link" :class="{ active: activeTab === 'view' }" @click="setActiveTab('view')" href="#">
                  <i class="bi bi-envelope-open me-2"></i>View Email History
                </a>
              </li>
            </ul>
            <!-- Loading State -->
            <div v-if="loading" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <!-- Automated Templates Tab -->
            <div v-if="activeTab === 'automated' && !loading" class="tab-content">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 class="mb-1">Automated Email Templates</h5>
                  <p class="text-muted mb-0">
                    System templates for automated notifications and communications.
                  </p>
                </div>
              </div>
              <!-- Show message if no automated templates -->
              <div v-if="automatedTemplates.length === 0" class="text-center py-5">
                <div class="mb-3">
                  <i class="bi bi-file-earmark-text display-4 text-muted"></i>
                </div>
                <h6 class="text-muted">No Automated Templates Found</h6>
                <p class="text-muted mb-3">Create automated templates for system notifications and communications.</p>
              </div>
              <!-- Automated templates list -->
              <div v-else class="template-links">
                <div v-for="template in automatedTemplates" :key="template.id" class="template-item mb-3">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <a href="#" class="template-link" @click.prevent="viewTemplate(template)">{{ template.name }}</a>
                      <div class="template-content mt-2">
                        <div v-if="template.description" class="text-muted small">{{ template.description }}</div>
                        <div class="text-muted small mt-1">
                          <strong>Subject:</strong> {{ template.subject }}
                        </div>
                        <div class="text-muted small">
                          <strong>Type:</strong>
                          <span class="badge bg-info text-dark ms-1">{{ template.type }}</span>
                        </div>
                        <div class="text-muted small">
                          <strong>Last updated:</strong> {{ formatDate(template.updated_at) }}
                        </div>
                      </div>
                    </div>
                    <div class="dropdown">
                      <button
                        class="btn btn-sm btn-outline-secondary dropdown-toggle"
                        type="button"
                        :id="'dropdown-' + template.id"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu" :aria-labelledby="'dropdown-' + template.id">
                        <li>
                          <button class="dropdown-item" @click="viewTemplate(template)">
                            <i class="bi bi-eye me-2"></i>View
                          </button>
                        </li>
<!--                        <li>-->
<!--                          <button class="dropdown-item" @click="editTemplate(template)">-->
<!--                            <i class="bi bi-pencil me-2"></i>Edit-->
<!--                          </button>-->
<!--                        </li>-->
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Custom Templates Tab -->
            <div v-if="activeTab === 'custom' && !loading" class="tab-content">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 class="mb-1">Custom Email Templates</h5>
                  <p class="text-muted mb-0">
                    Create and manage your custom email templates for personalized communications.
                  </p>
                </div>
                <button class="btn btn-primary" @click="showNewTemplateDialog('custom')">
                  <i class="bi bi-plus me-2"></i>New Custom Template
                </button>
              </div>
              <!-- Show message if no custom templates -->
              <div v-if="customTemplates.length === 0" class="text-center py-5">
                <div class="mb-3">
                  <i class="bi bi-file-earmark-plus display-4 text-muted"></i>
                </div>
                <h6 class="text-muted">No Custom Templates Found</h6>
                <p class="text-muted mb-3">Create custom templates for personalized communications.</p>
                <button class="btn btn-outline-primary" @click="showNewTemplateDialog('custom')">
                  <i class="bi bi-plus me-2"></i>Create First Custom Template
                </button>
              </div>
              <!-- Custom templates list -->
              <div v-else class="template-links">
                <div v-for="template in customTemplates" :key="template.id" class="template-item mb-3">
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="flex-grow-1">
                      <a href="#" class="template-link" @click.prevent="viewTemplate(template)">{{ template.name }}</a>
                      <div class="template-content mt-2">
                        <div v-if="template.description" class="text-muted small">{{ template.description }}</div>
                        <div class="text-muted small mt-1">
                          <strong>Subject:</strong> {{ template.subject }}
                        </div>
                        <div class="text-muted small">
                          <strong>Type:</strong>
                          <span class="badge bg-success text-white ms-1">{{ template.type }}</span>
                        </div>
                        <div class="text-muted small">
                          <strong>Last updated:</strong> {{ formatDate(template.updated_at) }}
                        </div>
                      </div>
                    </div>
                    <div class="dropdown">
                      <button
                        class="btn btn-sm btn-outline-secondary dropdown-toggle"
                        type="button"
                        :id="'dropdown-custom-' + template.id"
                        data-bs-toggle="dropdown"
                        aria-expanded="false"
                      >
                        <i class="bi bi-three-dots"></i>
                      </button>
                      <ul class="dropdown-menu" :aria-labelledby="'dropdown-custom-' + template.id">
                        <li>
                          <button class="dropdown-item" @click="viewTemplate(template)">
                            <i class="bi bi-eye me-2"></i>View
                          </button>
                        </li>
                        <li>
                          <button class="dropdown-item" @click="editTemplate(template)">
                            <i class="bi bi-pencil me-2"></i>Edit
                          </button>
                        </li>
                        <li><hr class="dropdown-divider"></li>
                        <li>
                          <button class="dropdown-item text-danger" @click="deleteTemplate(template)">
                            <i class="bi bi-trash me-2"></i>Delete
                          </button>
                        </li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <!-- Send Email Tab -->
            <div v-if="activeTab === 'send' && !loading" class="tab-content">
              <h5 class="mb-4">Send Email Message</h5>
              <form @submit.prevent="sendEmail">
                <div class="mb-3">
                  <label class="form-label">Send Type</label>
                  <div class="mb-3">
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="sendType"
                        id="specific"
                        value="specific"
                        v-model="composeEmail.sendType"
                        @change="handleSendTypeChange"
                        checked
                      >
                      <label class="form-check-label" for="specific">
                        Send to specific addresses
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="sendType"
                        id="group-call"
                        value="edctp_group"
                        v-model="composeEmail.sendType"
                        @change="handleSendTypeChange"
                      >
                      <label class="form-check-label" for="group-call">
                        Send to an EDCTP Call Group
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="sendType"
                        id="group-working"
                        value="working_group"
                        v-model="composeEmail.sendType"
                        @change="handleSendTypeChange"
                      >
                      <label class="form-check-label" for="group-working">
                        Send to a Working Group
                      </label>
                    </div>
                    <div class="form-check">
                      <input
                        class="form-check-input"
                        type="radio"
                        name="sendType"
                        id="all"
                        value="all"
                        v-model="composeEmail.sendType"
                        @change="handleSendTypeChange"
                      >
                      <label class="form-check-label" for="all">
                        Send to all users
                        <small class="text-muted d-block">Automatically includes all system users</small>
                      </label>
                    </div>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">
                    Email Addresses
                    <span v-if="composeEmail.sendType === 'all'" class="badge bg-info text-dark ms-2">
                      <i class="bi bi-people-fill me-1"></i>All Users Auto-Selected
                    </span>
                  </label>

                  <!-- EDCTP CALL GROUP DROPDOWN -->
                  <div class="mb-3" v-if="composeEmail.sendType === 'edctp_group'">
                    <label class="form-label">Select EDCTP Call Group</label>
                    <select
                      class="form-select"
                      v-model="composeEmail.edctpCall"
                      @change="loadEdctpCallEmails"
                    >
                      <option value="">-- Select EDCTP Call Group --</option>
                      <option
                        v-for="(label, key) in edctpCallMap"
                        :key="key"
                        :value="key"
                      >
                        {{ label }}
                      </option>
                    </select>
                    <small class="text-muted">
                      Only users with the <strong>Fellow</strong> role in the selected EDCTP call group will receive the email.
                    </small>
                  </div>

                  <!-- WORKING GROUP DROPDOWN -->
                  <div class="mb-3" v-if="composeEmail.sendType === 'working_group'">
                    <label class="form-label">Select Working Group</label>
                    <select
                      class="form-select"
                      v-model="composeEmail.workingGroupId"
                      @change="loadWorkingGroupEmails"
                    >
                      <option value="">-- Select Working Group --</option>
                      <option
                        v-for="group in userWorkingGroups"
                        :key="group.id"
                        :value="group.id"
                      >
                        {{ group.name }}
                      </option>
                    </select>
                    <small class="text-muted">
                      Only members of the selected working group will receive the email.
                    </small>
                  </div>

                  <div class="input-group">
                    <textarea
                      class="form-control"
                      v-model="composeEmail.addresses"
                      rows="3"
                      :placeholder="composeEmail.sendType === 'all'
                        ? 'All system users will be automatically included...'
                        : 'Enter email addresses separated by commas'"
                      :disabled="composeEmail.sendType === 'all'"
                      :class="{ 'bg-light': composeEmail.sendType === 'all' }"
                    ></textarea>

                    <div class="input-group-append" v-if="composeEmail.sendType === 'specific'">
                      <div class="btn-group-vertical">
                        <button
                          type="button"
                          class="btn btn-outline-primary btn-sm"
                          @click="showUserSelection"
                          :disabled="loadingUsers"
                          title="Select specific users"
                        >
                          <span v-if="loadingUsers" class="spinner-border spinner-border-sm me-1"></span>
                          <i v-else class="bi bi-people me-1"></i>
                          Select Users
                        </button>
                        <button
                          type="button"
                          class="btn btn-outline-secondary btn-sm"
                          @click="addAllUserEmails"
                          :disabled="loadingUsers"
                          title="Add all users to recipient list"
                        >
                          <span v-if="loadingUsers" class="spinner-border spinner-border-sm me-1"></span>
                          <i v-else class="bi bi-people-fill me-1"></i>
                          All Users
                        </button>
                      </div>
                    </div>
                  </div>

                  <small class="text-muted">
                    <span v-if="composeEmail.sendType === 'specific'">
                      Use the buttons above to populate with system users, or manually enter email addresses separated by commas.
                    </span>
                    <span v-else-if="composeEmail.sendType === 'all'">
                      <i class="bi bi-info-circle me-1"></i>
                      All system users ({{ allUsers.length || 'loading...' }}) will receive this email automatically.
                    </span>
                    <span v-else-if="composeEmail.sendType === 'edctp_group'">
                      Select an EDCTP Call group to send the email to all Fellows in that call.
                    </span>
                    <span v-else-if="composeEmail.sendType === 'working_group'">
                      Select a working group to send the email to all members of that group.
                    </span>
                  </small>
                </div>
                <div class="mb-3">
                  <label class="form-label">Template</label>
                  <select class="form-select" v-model="selectedTemplate" @change="loadTemplate">
                    <option value="">Custom (No Template)</option>
                    <optgroup v-if="customTemplates.length > 0" label="Custom Templates">
                      <option v-for="template in customTemplates" :key="'custom-' + template.id" :value="template.id">
                        {{ template.name }}
                      </option>
                    </optgroup>
                    <optgroup v-if="automatedTemplates.length > 0" label="Automated Templates">
                      <option v-for="template in automatedTemplates" :key="'automated-' + template.id" :value="template.id">
                        {{ template.name }}
                      </option>
                    </optgroup>
                  </select>
                  <small class="text-muted">
                    Choose a template to pre-fill the subject and content, or select "Custom" to compose manually.
                  </small>
                  <div v-if="selectedTemplate" class="mt-2 p-2 bg-light rounded">
                    <div class="d-flex align-items-center">
                      <i class="bi bi-info-circle text-primary me-2"></i>
                      <small class="text-muted">
                        <strong>Template:</strong>
                        {{ (customTemplates.find(t => t.id === selectedTemplate) || automatedTemplates.find(t => t.id === selectedTemplate))?.name }}
                        <span class="badge ms-2" :class="getTemplateTypeBadgeClass()">
                          {{ (customTemplates.find(t => t.id === selectedTemplate) || automatedTemplates.find(t => t.id === selectedTemplate))?.type }}
                        </span>
                      </small>
                    </div>
                  </div>
                </div>
                <div class="mb-3">
                  <label class="form-label">Subject</label>
                  <input type="text" class="form-control" v-model="composeEmail.subject" required>
                </div>
                <div class="mb-3">
                  <label class="form-label">Message</label>
                  <div class="rich-text-editor">
                    <div class="editor-toolbar">

                      <!-- Undo / Redo -->
                      <div class="toolbar-group">
                        <button type="button" class="toolbar-btn" @click="emailExec('undo')">
                          <i class="bi bi-arrow-counterclockwise"></i>
                        </button>
                        <button type="button" class="toolbar-btn" @click="emailExec('redo')">
                          <i class="bi bi-arrow-clockwise"></i>
                        </button>
                      </div>

                      <div class="toolbar-divider"></div>

                      <!-- Text formatting -->
                      <div class="toolbar-group">
                        <button type="button" class="toolbar-btn" @click="emailExec('bold')"><b>B</b></button>
                        <button type="button" class="toolbar-btn" @click="emailExec('italic')"><i>I</i></button>
                        <button type="button" class="toolbar-btn" @click="emailExec('underline')"><u>U</u></button>
                        <button type="button" class="toolbar-btn" @click="emailExec('strikeThrough')"><s>S</s></button>
                      </div>

                      <div class="toolbar-divider"></div>

                      <!-- Lists -->
                      <div class="toolbar-group">
                        <button type="button" class="toolbar-btn" @click="emailExec('insertUnorderedList')">•</button>
                        <button type="button" class="toolbar-btn" @click="emailExec('insertOrderedList')">1.</button>
                      </div>

                      <div class="toolbar-divider"></div>

                      <!-- Alignment -->
                      <div class="toolbar-group">
                        <button type="button" class="toolbar-btn" @click="emailExec('justifyLeft')">⌐</button>
                        <button type="button" class="toolbar-btn" @click="emailExec('justifyCenter')">≡</button>
                        <button type="button" class="toolbar-btn" @click="emailExec('justifyRight')">¬</button>
                      </div>

                      <div class="toolbar-divider"></div>

                      <!-- Insert -->
                      <div class="toolbar-group">
                        <button type="button" class="toolbar-btn" @click="emailInsertLink">
                          <i class="bi bi-link"></i>
                        </button>

                        <button type="button" class="toolbar-btn" @click="emailInsertImage">
                          <i class="bi bi-image"></i>
                        </button>

                        <button type="button" class="toolbar-btn" @click="emailInsertCode">
                          <i class="bi bi-code"></i>
                        </button>
                      </div>

                    </div>

                    <div
                      ref="emailEditor"
                      class="editor-content"
                      contenteditable="true"
                      @input="updateEmailBody"
                      @paste="handleEmailPaste"
                    ></div>
                  </div>
                </div>
                <div class="d-flex justify-content-center">
                  <button type="submit" class="btn btn-primary px-4" :disabled="sendingEmail">
                    <span v-if="sendingEmail" class="spinner-border spinner-border-sm me-2" role="status"></span>
                    Send Now
                  </button>
                </div>
              </form>
            </div>

            <!-- NEW: View Emails Tab -->
            <div v-if="activeTab === 'view' && !loading" class="tab-content">
              <div class="d-flex justify-content-between align-items-center mb-4">
                <div>
                  <h5 class="mb-1">Email Viewer</h5>
                  <p class="text-muted mb-0">
                    Browse and view detailed information about sent emails.
                  </p>
                </div>
                <div class="d-flex gap-2">
                  <button class="btn btn-outline-secondary" @click="refreshEmailList">
                    <i class="bi bi-arrow-clockwise me-2"></i>Refresh
                  </button>
                  <div class="dropdown">
                    <button class="btn btn-outline-primary dropdown-toggle" type="button" data-bs-toggle="dropdown">
                      <i class="bi bi-funnel me-2"></i>Filter: {{ emailFilter }}
                    </button>
                    <ul class="dropdown-menu">
                      <li><button class="dropdown-item" @click="setEmailFilter('all')">All Emails</button></li>
                      <li><button class="dropdown-item" @click="setEmailFilter('sent')">Sent Only</button></li>
                      <li><button class="dropdown-item" @click="setEmailFilter('pending')">Pending Only</button></li>
                      <li><button class="dropdown-item" @click="setEmailFilter('failed')">Failed Only</button></li>
                    </ul>
                  </div>
                </div>
              </div>

              <!-- Search Bar -->
              <div class="mb-4">
                <strong>Subject:</strong> {{ previewedTemplateSubject || viewingTemplate.subject || 'No subject' }}
                  <span class="input-group-text">
                    <i class="bi bi-search"></i>
                  </span>
                  <input
                    type="text"
                    class="form-control"
                    placeholder="Search emails by recipient, subject, or content..."
                    v-model="emailSearchQuery"
                    @input="searchEmails"
                  >
                  <button v-if="emailSearchQuery" class="btn btn-outline-secondary" type="button" @click="clearSearch">
                    <i class="bi bi-x"></i>
                  </button>
                </div>
              </div>

              <!-- Email Stats Cards -->
              <div class="row mb-4">
                <div class="col-md-3">
                  <div class="stats-card-email">
                    <div class="stats-icon bg-success">
                      <i class="bi bi-check-circle"></i>
                    </div>
                    <div class="stats-content">
                      <h6>Sent</h6>
                      <h4>{{ getEmailCountByStatus('sent') }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="stats-card-email">
                    <div class="stats-icon bg-warning">
                      <i class="bi bi-clock"></i>
                    </div>
                    <div class="stats-content">
                      <h6>Pending</h6>
                      <h4>{{ getEmailCountByStatus('pending') }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="stats-card-email">
                    <div class="stats-icon bg-danger">
                      <i class="bi bi-exclamation-circle"></i>
                    </div>
                    <div class="stats-content">
                      <h6>Failed</h6>
                      <h4>{{ getEmailCountByStatus('failed') }}</h4>
                    </div>
                  </div>
                </div>
                <div class="col-md-3">
                  <div class="stats-card-email">
                    <div class="stats-icon bg-info">
                      <i class="bi bi-envelope"></i>
                    </div>
                    <div class="stats-content">
                      <h6>Total</h6>
                      <h4>{{ filteredEmails.length }}</h4>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Email List -->
              <div v-if="filteredEmails.length === 0" class="text-center py-5">
                <div class="mb-3">
                  <i class="bi bi-envelope display-4 text-muted"></i>
                </div>
                <h6 class="text-muted">No Emails Found</h6>
                <p class="text-muted mb-3">
                  {{ emailSearchQuery ? 'No emails match your search criteria.' : 'No emails have been sent yet.' }}
                </p>
                <button v-if="emailSearchQuery" class="btn btn-outline-primary" @click="clearSearch">
                  <i class="bi bi-arrow-left me-2"></i>Clear Search
                </button>
              </div>

              <div v-else class="email-list">
                <div
                  v-for="email in paginatedEmails"
                  :key="email.id"
                  class="email-item"
                  @click="viewEmailDetails(email)"
                >
                  <div class="email-header">
                    <div class="email-recipient">
                      <i class="bi bi-person-circle me-2"></i>
                      <strong>{{ email.recipient }}</strong>
                      <span class="badge ms-2" :class="getStatusClass(email.status)">
                        {{ email.status }}
                      </span>
                    </div>
                    <div class="email-date">
                      <i class="bi bi-calendar me-1"></i>
                      {{ formatDate(email.created_at) }}
                    </div>
                  </div>
                  <div class="email-subject">
                    <i class="bi bi-envelope me-2"></i>
                    {{ email.subject }}
                  </div>
                  <div class="email-content-preview">
                    {{ getContentPreview(email.content) }}
                  </div>
                  <div class="email-footer" v-if="email.type_name || email.error_message">
                    <div class="email-type" v-if="email.type_name">
                      <i class="bi bi-tag me-1"></i>
                      <span class="badge bg-secondary">{{ email.type_name }}</span>
                    </div>
                    <div class="email-error" v-if="email.error_message">
                      <i class="bi bi-exclamation-triangle text-danger me-1"></i>
                      <small class="text-danger">{{ email.error_message }}</small>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Pagination -->
              <div v-if="filteredEmails.length > emailsPerPage" class="d-flex justify-content-center mt-4">
                <nav>
                  <ul class="pagination">
                    <li class="page-item" :class="{ disabled: currentEmailPage === 1 }">
                      <button class="page-link" @click="setEmailPage(currentEmailPage - 1)">
                        ‹
                      </button>
                    </li>

                    <li
                      v-for="p in visiblePaginationPages"
                      :key="p"
                      class="page-item"
                      :class="{ active: p === currentEmailPage, disabled: p === '...' }"
                    >
                      <button
                        class="page-link"
                        @click="p !== '...' && setEmailPage(p)"
                      >
                        {{ p }}
                      </button>
                    </li>

                    <li class="page-item" :class="{ disabled: currentEmailPage === totalEmailPages }">
                      <button class="page-link" @click="setEmailPage(currentEmailPage + 1)">
                        ›
                      </button>
                    </li>
                  </ul>
                </nav>
              </div>
            </div>

            <!-- User Selection Dialog -->
            <div
              v-if="showUserListDialog"
              class="modal fade show"
              style="display: block; background-color: rgba(0,0,0,0.5);"
              data-bs-backdrop="static"
              data-bs-keyboard="false"
            >
              <div class="modal-dialog modal-lg modal-dialog-centered">
                <div class="modal-content">
                  <div class="modal-header">
                    <h5 class="modal-title">
                      <i class="bi bi-people me-2"></i>
                      Select Users for Email
                    </h5>
                    <button
                      type="button"
                      class="btn-close"
                      @click="showUserListDialog = false"
                      aria-label="Close"
                    ></button>
                  </div>
                  <div class="modal-body">
                    <div v-if="loadingUsers" class="text-center py-4">
                      <div class="spinner-border text-primary" role="status">
                        <span class="visually-hidden">Loading users...</span>
                      </div>
                      <p class="mt-2 text-muted">Loading users...</p>
                    </div>
                    <div v-else-if="allUsers.length === 0" class="text-center py-4">
                      <i class="bi bi-people display-4 text-muted"></i>
                      <h6 class="text-muted mt-3">No Users Found</h6>
                      <p class="text-muted">No users with email addresses found in the system.</p>
                    </div>
                    <div v-else>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <p class="mb-0 text-muted">
                          Found {{ allUsers.length }} users. Select recipients for your email.
                        </p>
                        <button
                          type="button"
                          class="btn btn-sm btn-outline-primary"
                          @click="selectAllUsers"
                        >
                          <i class="bi bi-check-all me-1"></i>
                          {{ selectedUsers.length === allUsers.length ? 'Deselect All' : 'Select All' }}
                        </button>
                      </div>
                      <div class="user-list-container" style="max-height: 400px; overflow-y: auto;">
                        <div class="list-group">
                          <div
                            v-for="user in allUsers"
                            :key="user.id"
                            class="list-group-item list-group-item-action"
                            :class="{ active: isUserSelected(user.id) }"
                            @click="toggleUserSelection(user.id)"
                            style="cursor: pointer;"
                          >
                            <div class="d-flex align-items-center">
                              <input
                                type="checkbox"
                                class="form-check-input me-3"
                                :checked="isUserSelected(user.id)"
                                @click.stop="toggleUserSelection(user.id)"
                              >
                              <div class="flex-grow-1">
                                <div class="d-flex justify-content-between align-items-start">
                                  <div>
                                    <h6 class="mb-1">{{ getUserDisplayName(user) }}</h6>
                                    <p class="mb-1 text-muted">{{ user.email }}</p>
                                    <small class="text-muted">
                                      <span v-if="user.role" class="badge bg-secondary ms-2">{{ user.role }}</span>
                                    </small>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      <div v-if="selectedUsers.length > 0" class="mt-3 p-2 bg-light rounded">
                        <small class="text-muted">
                          <i class="bi bi-check-circle text-success me-1"></i>
                          {{ selectedUsers.length }} user{{ selectedUsers.length !== 1 ? 's' : '' }} selected
                        </small>
                      </div>
                    </div>
                  </div>
                  <div class="modal-footer">
                    <button type="button" class="btn btn-secondary" @click="showUserListDialog = false">
                      Cancel
                    </button>
                    <button
                      type="button"
                      class="btn btn-primary"
                      @click="addSelectedUserEmails"
                      :disabled="selectedUsers.length === 0"
                    >
                      <i class="bi bi-plus me-1"></i>
                      Add Selected ({{ selectedUsers.length }})
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <!-- Email Management Tab -->
            <div v-if="activeTab === 'management' && !loading" class="tab-content">
              <h5 class="mb-4">Email Log Management</h5>
              <div class="table-responsive">
                <table class="table table-hover">
                  <thead>
                  <tr>
                    <th>TYPE</th>
                    <th>RECIPIENT</th>
                    <th>SUBJECT</th>
                    <th>CONTENT</th>
                    <th>STATUS</th>
                    <th>ERROR</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="email in sentEmails" :key="email.id">
                    <td>{{ email.type_name || email.type }}</td>
                    <td>{{ email.recipient }}</td>
                    <td>{{ email.subject }}</td>
                    <td>
                      <div class="content-preview" @click="viewEmailContent(email)">
                        {{ getContentPreview(email.content) }}
                      </div>
                    </td>
                    <td>
                      <span class="badge" :class="getStatusClass(email.status)">
                        {{ email.status }}
                      </span>
                    </td>
                    <td>{{ email.error_message || '-' }}</td>
                  </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    <!-- Template View Dialog -->
    <div
      v-if="showViewDialog"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-eye me-2"></i>
              View Template{{ viewingTemplate?.name ? ' - ' + viewingTemplate.name : '' }}
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showViewDialog = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <!-- Loading state -->
            <div v-if="!viewingTemplate" class="text-center py-5">
              <div class="spinner-border text-primary" role="status">
                <span class="visually-hidden">Loading template...</span>
              </div>
              <p class="mt-2 text-muted">Loading template details...</p>
            </div>

            <!-- Error state -->
            <div v-else-if="viewingTemplate.error" class="text-center py-5">
              <div class="mb-3">
                <i class="bi bi-exclamation-triangle display-4 text-warning"></i>
              </div>
              <h6 class="text-muted">Template Data Not Available</h6>
              <p class="text-muted mb-3">{{ viewingTemplate.message || 'Unable to load template details. Please try again.' }}</p>
              <button class="btn btn-outline-primary" @click="showViewDialog = false">
                Close
              </button>
            </div>

            <!-- Template content -->
            <div v-else class="template-view-content">
              <div class="row mb-4">
                <div class="col-md-8">
                  <div class="template-info">
                    <div class="info-item mb-3">
                      <label class="info-label">
                        <i class="bi bi-tag me-2"></i>Template Name
                      </label>
                      <div class="info-value">{{ viewingTemplate.name || 'Untitled Template' }}</div>
                    </div>

                    <div v-if="viewingTemplate.description" class="info-item mb-3">
                      <label class="info-label">
                        <i class="bi bi-info-circle me-2"></i>Description
                      </label>
                      <div class="info-value">{{ viewingTemplate.description }}</div>
                    </div>

                    <div class="info-item mb-3">
                      <label class="info-label">
                        <i class="bi bi-envelope me-2"></i>Subject Line
                      </label>
                      <div class="info-value">{{ viewingTemplate.subject || 'No subject' }}</div>
                    </div>
                  </div>
                </div>

                <div class="col-md-4">
                  <div class="template-meta">
                    <div class="meta-card">
                      <div class="meta-item">
                        <strong>Type:</strong>
                        <span class="badge ms-2" :class="viewingTemplate.type === 'automated' ? 'bg-info text-dark' : 'bg-success text-white'">
                          {{ viewingTemplate.type || 'custom' }}
                        </span>
                      </div>

                      <div v-if="viewingTemplate.created_at" class="meta-item">
                        <strong>Created:</strong>
                        <div class="text-muted small">{{ formatDate(viewingTemplate.created_at) }}</div>
                      </div>

                      <div v-if="viewingTemplate.updated_at" class="meta-item">
                        <strong>Last Updated:</strong>
                        <div class="text-muted small">{{ formatDate(viewingTemplate.updated_at) }}</div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>

              <!-- Content Section -->
              <div class="template-content-section">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <label class="info-label mb-0">
                    <i class="bi bi-file-text me-2"></i>Template Content
                  </label>
                  <div class="btn-group" role="group">
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="templateViewMode === 'rendered' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="templateViewMode = 'rendered'"
                    >
                      <i class="bi bi-eye me-1"></i>Preview
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="templateViewMode === 'source' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="templateViewMode = 'source'"
                    >
                      <i class="bi bi-code me-1"></i>Source
                    </button>
                  </div>
                </div>

                <!-- Rendered Preview -->
                <div v-if="templateViewMode === 'rendered'" class="template-rendered-preview">
                  <div class="preview-container" v-html="previewedTemplateBody || viewingTemplate.body || 'No content available'"></div>
                </div>

                <!-- Source Code -->
                <div v-else class="template-source-preview">
                  <pre class="source-code"><code>{{ previewedTemplateBody || viewingTemplate.body || 'No content available' }}</code></pre>
                </div>
              </div>

              <div class="row mt-4">
                <div class="col-md-8">
                  <label class="form-label">
                    <i class="bi bi-braces me-1"></i>Preview Variables (JSON)
                  </label>
                  <textarea
                    v-model="templatePreviewVariablesText"
                    class="form-control"
                    rows="3"
                    placeholder='{"user_name":"Test User"}'
                  ></textarea>
                </div>
                <div class="col-md-4">
                  <label class="form-label">
                    <i class="bi bi-envelope-check me-1"></i>Test Email Address
                  </label>
                  <input
                    v-model="templateTestEmail"
                    type="email"
                    class="form-control"
                    placeholder="admin@example.com"
                  >
                </div>
              </div>

              <div v-if="templatePreviewVariablesUsed.length > 0" class="template-variables mt-3">
                <label class="info-label">
                  <i class="bi bi-tags me-2"></i>Variables Used
                </label>
                <div class="variables-list">
                  <span
                    v-for="variable in templatePreviewVariablesUsed"
                    :key="variable"
                    class="badge bg-light text-dark me-2 mb-2"
                  >
                    {{ variable }}
                  </span>
                </div>
              </div>

              <!-- Template Variables Info (if any) -->
<!--              <div v-if="getTemplateVariables(viewingTemplate.body).length > 0" class="template-variables mt-4">-->
<!--                <label class="info-label">-->
<!--                  <i class="bi bi-braces me-2"></i>Template Variables-->
<!--                </label>-->
<!--                <div class="variables-list">-->
<!--                  <span-->
<!--                    v-for="variable in getTemplateVariables(viewingTemplate.body)"-->
<!--                    :key="variable"-->
<!--                    class="badge bg-light text-dark me-2 mb-2"-->
<!--                  >-->
<!--                    {{ variable }}-->
<!--                  </span>-->
<!--                </div>-->
<!--                <small class="text-muted">These variables will be replaced with actual values when the email is sent.</small>-->
<!--              </div>-->
            </div>
          </div>
          <div class="modal-footer">
<!--            <button-->
<!--              v-if="viewingTemplate"-->
<!--              type="button"-->
<!--              class="btn btn-outline-primary me-2"-->
<!--              @click="editTemplate(viewingTemplate)"-->
<!--            >-->
<!--              <i class="bi bi-pencil me-1"></i>Edit Template-->
<!--            </button>-->
            <button
              v-if="viewingTemplate"
              type="button"
              class="btn btn-outline-primary me-2"
              :disabled="templatePreviewLoading"
              @click="loadTemplatePreviewFromInput"
            >
              <i class="bi bi-eye me-1"></i>{{ templatePreviewLoading ? 'Loading...' : 'Refresh Preview' }}
            </button>
            <button
              v-if="viewingTemplate"
              type="button"
              class="btn btn-outline-secondary me-2"
              @click="copyTemplateContent"
            >
              <i class="bi bi-clipboard me-1"></i>Copy HTML
            </button>
            <button
              v-if="viewingTemplate"
              type="button"
              class="btn btn-primary me-2"
              :disabled="templateTestSending"
              @click="sendTemplateTest"
            >
              <i class="bi bi-send me-1"></i>{{ templateTestSending ? 'Sending...' : 'Send Test' }}
            </button>
            <button type="button" class="btn btn-secondary" @click="showViewDialog = false">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Template Edit/Create Dialog -->
    <div
      v-if="showTemplateDialog"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-file-earmark-text me-2"></i>
              {{ editingTemplate ? 'Edit' : 'Create' }} Template
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showTemplateDialog = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <form @submit.prevent="saveTemplate">
              <div class="mb-3">
                <label class="form-label">Template Name *</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="templateForm.name"
                  required
                  :class="{ 'is-invalid': formErrors.name }"
                  placeholder="Enter template name"
                >
                <div v-if="formErrors.name" class="invalid-feedback">
                  {{ formErrors.name }}
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label">Subject *</label>
                <input
                  type="text"
                  class="form-control"
                  v-model="templateForm.subject"
                  required
                  :class="{ 'is-invalid': formErrors.subject }"
                  placeholder="Email subject line"
                >
                <div v-if="formErrors.subject" class="invalid-feedback">
                  {{ formErrors.subject }}
                </div>
                <small class="text-muted">
                  You can use variables like {{name}}, {{email}}, etc.
                </small>
              </div>
              <div class="mb-3">
                <label class="form-label">Content *</label>

                <div class="rich-text-editor" :class="{ 'is-invalid': formErrors.body }">

                  <div class="editor-toolbar">

                    <!-- Undo / Redo -->
                    <div class="toolbar-group">
                      <button type="button" class="toolbar-btn" @click="templateExec('undo')">
                        <i class="bi bi-arrow-counterclockwise"></i>
                      </button>
                      <button type="button" class="toolbar-btn" @click="templateExec('redo')">
                        <i class="bi bi-arrow-clockwise"></i>
                      </button>
                    </div>

                    <div class="toolbar-divider"></div>

                    <!-- Formatting -->
                    <div class="toolbar-group">
                      <button type="button" class="toolbar-btn" @click="templateExec('bold')"><b>B</b></button>
                      <button type="button" class="toolbar-btn" @click="templateExec('italic')"><i>I</i></button>
                      <button type="button" class="toolbar-btn" @click="templateExec('underline')"><u>U</u></button>
                      <button type="button" class="toolbar-btn" @click="templateExec('strikeThrough')"><s>S</s></button>
                    </div>

                    <div class="toolbar-divider"></div>

                    <!-- Lists -->
                    <div class="toolbar-group">
                      <button type="button" class="toolbar-btn" @click="templateExec('insertUnorderedList')">•</button>
                      <button type="button" class="toolbar-btn" @click="templateExec('insertOrderedList')">1.</button>
                    </div>

                    <div class="toolbar-divider"></div>

                    <!-- Insert -->
                    <div class="toolbar-group">
                      <button type="button" class="toolbar-btn" @click="templateInsertLink">
                        <i class="bi bi-link"></i>
                      </button>

                      <button type="button" class="toolbar-btn" @click="templateInsertImage">
                        <i class="bi bi-image"></i>
                      </button>

                      <button type="button" class="toolbar-btn" @click="templateInsertCode">
                        <i class="bi bi-code"></i>
                      </button>
                    </div>

                  </div>

                  <div
                    ref="templateEditor"
                    class="editor-content"
                    contenteditable="true"
                    @input="updateTemplateBody"
                    @paste="handleTemplatePaste"
                  ></div>

                </div>

                <div v-if="formErrors.body" class="invalid-feedback d-block">
                  {{ formErrors.body }}
                </div>
              </div>
              <div v-if="!editingTemplate" class="mb-3">
                <label class="form-label">Template Type</label>
                <select class="form-select" v-model="templateForm.type" disabled>
                  <option value="custom">Custom</option>
                </select>
                <small class="text-muted">New templates are automatically created as custom templates</small>
              </div>
              <div v-else-if="editingTemplate && editingTemplate.type === 'custom'" class="mb-3" hidden="">
                <label class="form-label">Template Type</label>
                <select class="form-select" v-model="templateForm.type">
                  <option value="automated">Custom</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
              <div v-else-if="editingTemplate && editingTemplate.type !== 'custom'" class="mb-3">
                <label class="form-label">Template Type</label>
                <input type="text" class="form-control" :value="templateForm.type" disabled>
                <small class="text-muted">Automated template types cannot be changed</small>
              </div>
            </form>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showTemplateDialog = false">
              Cancel
            </button>
            <button type="button" class="btn btn-primary" @click="saveTemplate" :disabled="savingTemplate">
              <span v-if="savingTemplate" class="spinner-border spinner-border-sm me-2" role="status"></span>
              {{ editingTemplate ? 'Update' : 'Create' }} Template
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Email Content View Dialog -->
    <div
      v-if="showEmailContentDialog"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-envelope me-2"></i>
              Email Content
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showEmailContentDialog = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="viewingEmailContent">
              <div class="mb-3">
                <strong>To:</strong> {{ viewingEmailContent.recipient }}
              </div>
              <div class="mb-3">
                <strong>Subject:</strong> {{ viewingEmailContent.subject }}
              </div>
              <div class="mb-3">
                <strong>Status:</strong>
                <span class="badge ms-2" :class="getStatusClass(viewingEmailContent.status)">
                  {{ viewingEmailContent.status }}
                </span>
              </div>
              <div class="mb-3">
                <strong>Content:</strong>
                <div class="email-content-preview" v-html="viewingEmailContent.content"></div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showEmailContentDialog = false">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- NEW: Email Details Dialog -->
    <div
      v-if="showEmailDetailsDialog"
      class="modal fade show"
      style="display: block; background-color: rgba(0,0,0,0.5);"
      data-bs-backdrop="static"
      data-bs-keyboard="false"
    >
      <div class="modal-dialog modal-xl modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title">
              <i class="bi bi-envelope-open me-2"></i>
              Email Details
            </h5>
            <button
              type="button"
              class="btn-close"
              @click="showEmailDetailsDialog = false"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedEmailDetails">
              <!-- Email Header Info -->
              <div class="row mb-4">
                <div class="col-md-8">
                  <div class="email-details-header">
                    <div class="d-flex align-items-center mb-2">
                      <i class="bi bi-person-circle text-primary me-2"></i>
                      <h6 class="mb-0">Recipient</h6>
                    </div>
                    <p class="mb-3">{{ selectedEmailDetails.recipient }}</p>

                    <div class="d-flex align-items-center mb-2">
                      <i class="bi bi-envelope text-primary me-2"></i>
                      <h6 class="mb-0">Subject</h6>
                    </div>
                    <p class="mb-3">{{ selectedEmailDetails.subject }}</p>
                  </div>
                </div>
                <div class="col-md-4">
                  <div class="email-meta-info">
                    <div class="meta-item">
                      <strong>Status:</strong>
                      <span class="badge ms-2" :class="getStatusClass(selectedEmailDetails.status)">
                        <i class="bi" :class="getStatusIcon(selectedEmailDetails.status)"></i>
                        {{ selectedEmailDetails.status }}
                      </span>
                    </div>
                    <div class="meta-item" v-if="selectedEmailDetails.type_name">
                      <strong>Type:</strong>
                      <span class="badge bg-secondary ms-2">{{ selectedEmailDetails.type_name }}</span>
                    </div>
                    <div class="meta-item">
                      <strong>Sent:</strong>
                      <span class="ms-2">{{ formatDetailedDate(selectedEmailDetails.created_at) }}</span>
                    </div>
                    <div class="meta-item" v-if="selectedEmailDetails.updated_at !== selectedEmailDetails.created_at">
                      <strong>Updated:</strong>
                      <span class="ms-2">{{ formatDetailedDate(selectedEmailDetails.updated_at) }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Error Message (if any) -->
              <div v-if="selectedEmailDetails.error_message" class="alert alert-danger mb-4">
                <div class="d-flex align-items-center">
                  <i class="bi bi-exclamation-triangle me-2"></i>
                  <strong>Error Message:</strong>
                </div>
                <p class="mb-0 mt-2">{{ selectedEmailDetails.error_message }}</p>
              </div>

              <!-- Email Content -->
              <div class="email-content-section">
                <div class="d-flex align-items-center justify-content-between mb-3">
                  <h6 class="mb-0">
                    <i class="bi bi-file-text text-primary me-2"></i>
                    Email Content
                  </h6>
                  <div class="btn-group" role="group">
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="emailViewMode === 'rendered' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="emailViewMode = 'rendered'"
                    >
                      <i class="bi bi-eye me-1"></i>Rendered
                    </button>
                    <button
                      type="button"
                      class="btn btn-sm"
                      :class="emailViewMode === 'source' ? 'btn-primary' : 'btn-outline-secondary'"
                      @click="emailViewMode = 'source'"
                    >
                      <i class="bi bi-code me-1"></i>Source
                    </button>
                  </div>
                </div>

                <!-- Rendered View -->
                <div v-if="emailViewMode === 'rendered'" class="email-rendered-content">
                  <div class="rendered-email-container" v-html="selectedEmailDetails.content"></div>
                </div>

                <!-- Source View -->
                <div v-else class="email-source-content">
                  <pre class="source-code"><code>{{ selectedEmailDetails.content }}</code></pre>
                </div>
              </div>

              <!-- Additional Actions -->
              <div class="email-actions mt-4">
                <div class="d-flex justify-content-between align-items-center">
                  <div class="email-id-info">
                    <small class="text-muted">
                      <i class="bi bi-hash me-1"></i>
                      Email ID: {{ selectedEmailDetails.id }}
                    </small>
                  </div>
                  <div>
                    <button
                      v-if="selectedEmailDetails.status === 'failed'"
                      class="btn btn-outline-warning btn-sm me-2"
                      @click="resendEmail(selectedEmailDetails)"
                    >
                      <i class="bi bi-arrow-clockwise me-1"></i>Retry Send
                    </button>
                    <button class="btn btn-outline-secondary btn-sm" @click="copyEmailContent">
                      <i class="bi bi-clipboard me-1"></i>Copy Content
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-secondary" @click="showEmailDetailsDialog = false">
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
    <!-- Toast Notification -->
    <div class="toast-container position-fixed top-0 end-0 p-3" style="z-index: 9999;">
      <div class="toast" :class="{ show: toast.show, 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error' }" role="alert">
        <div class="toast-header" :class="{ 'bg-success': toast.type === 'success', 'bg-danger': toast.type === 'error', 'text-white': true }">
          <i class="bi me-2" :class="toast.type === 'success' ? 'bi-check-circle-fill' : 'bi-exclamation-triangle-fill'"></i>
          <strong class="me-auto">{{ toast.type === 'success' ? 'Success' : 'Error' }}</strong>
          <button type="button" class="btn-close btn-close-white" @click="toast.show = false"></button>
        </div>
        <div class="toast-body text-white">
          {{ toast.message }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EmailService from '@/services/emails/EmailService'
import UserService from '@/services/users/UserService'
import WorkingGroupServices from '@/services/workinggroups/WorkingGroupServices'

export default {
  name: 'EmailAdmin',
  data() {
    return {
      allUsers: [],
      loadingUsers: false,
      showUserListDialog: false,
      selectedUsers: [],
      activeTab: 'automated',
      loading: false,
      sendingEmail: false,
      savingTemplate: false,
      showTemplateDialog: false,
      showViewDialog: false,
      showEmailContentDialog: false,
      showEmailDetailsDialog: false,
      editingTemplate: null,
      viewingTemplate: null,
      viewingEmailContent: null,
      selectedEmailDetails: null,
      selectedTemplate: '',
      currentTemplateType: 'automated',
      emailViewMode: 'rendered',
      templateViewMode: 'rendered',
      templatePreviewVariablesText: '{}',
      templatePreviewLoading: false,
      templateTestEmail: '',
      templateTestSending: false,
      previewedTemplateSubject: '',
      previewedTemplateBody: '',
      templatePreviewVariablesUsed: [],
      toast: {
        show: false,
        type: 'success',
        message: ''
      },
      userWorkingGroups: [],
      loadingWorkingGroups: false,

      edctpCallMap: {
        1: 'Career Development Fellowship (CDF)',
        2: 'Senior Fellowship (SF)',
        3: 'EDCTP Clinical Research & Development Fellowship (R&D F)',
        4: 'EDCTP-AREF Preparatory Fellowships (PF)',
        5: 'Senior Fellowship Plus (SFP)',
        6: 'Disease Outbreak and Epidemic Response (EPI Fellows)',
        7: 'Female PhD Fellowships',
        8: "EDCTP Master's Fellowship"
      },

      // Email viewing data
      emailFilter: 'all',
      emailSearchQuery: '',
      filteredEmails: [],
      currentEmailPage: 1,
      emailsPerPage: 10,

      composeEmail: {
        addresses: '',
        sendType: 'specific',
        subject: '',
        body: '',
        attachments: [],
        edctpCall: '',
        workingGroupId: ''
      },

      templateForm: {
        name: '',
        subject: '',
        body: '',
        type: 'custom'
      },
      formErrors: {},
      automatedTemplates: [],
      customTemplates: [],
      sentEmails: []
    }
  },

  computed: {
    paginatedEmails() {
      const start = (this.currentEmailPage - 1) * this.emailsPerPage
      return this.filteredEmails.slice(start, start + this.emailsPerPage)
    },

    totalEmailPages() {
      return Math.ceil(this.filteredEmails.length / this.emailsPerPage)
    },

    visiblePaginationPages() {
      let pages = []
      let total = this.totalEmailPages
      let current = this.currentEmailPage

      // Always show first page
      pages.push(1)

      // If page gap between 1 and current - 2
      if (current > 4) pages.push('...')

      // Show ±2 window around current page
      for (let i = current - 2; i <= current + 2; i++) {
        if (i > 1 && i < total) pages.push(i)
      }

      // Last page gap
      if (current < total - 3) pages.push('...')

      // Always show last page (if >1)
      if (total > 1) pages.push(total)

      return pages
    }
  },

  async mounted() {
    await this.loadInitialData()
  },

  methods: {
    showToast(type, message) {
      this.toast = {
        show: true,
        type: type,
        message: message
      }
      setTimeout(() => {
        this.toast.show = false
      }, 3000)
    },

    /* ===============================
      EMAIL WYSIWYG EDITOR
    ================================ */

    focusEmailEditor() {
      if (this.$refs.emailEditor) {
        this.$refs.emailEditor.focus()
      }
    },

    emailExec(command, value = null) {
      this.focusEmailEditor()
      document.execCommand(command, false, value)
      this.updateEmailBody()
    },

    updateEmailBody() {
      if (this.$refs.emailEditor) {
        this.composeEmail.body = this.$refs.emailEditor.innerHTML
      }
    },

    handleEmailPaste(event) {
      event.preventDefault()

      const text = (event.clipboardData || window.clipboardData)
        .getData('text/plain')

      document.execCommand('insertText', false, text)

      this.updateEmailBody()
    },

    emailInsertLink() {
      this.focusEmailEditor()
      const url = prompt('Enter link URL (https://...)')
      if (!url) return
      document.execCommand('createLink', false, url)
      this.updateEmailBody()
    },

    emailInsertImage() {
      this.focusEmailEditor()
      const url = prompt('Enter image URL')
      if (!url) return
      document.execCommand('insertImage', false, url)
      this.updateEmailBody()
    },

    /* ===============================
      TEMPLATE WYSIWYG EDITOR
    =============================== */

    focusTemplateEditor() {
      if (this.$refs.templateEditor) {
        this.$refs.templateEditor.focus()
      }
    },

    templateExec(command, value = null) {
      this.focusTemplateEditor()
      document.execCommand(command, false, value)
      this.updateTemplateBody()
    },

    updateTemplateBody() {
      if (this.$refs.templateEditor) {
        this.templateForm.body = this.$refs.templateEditor.innerHTML
      }
    },

    handleTemplatePaste(event) {
      event.preventDefault()

      const text = (event.clipboardData || window.clipboardData)
        .getData('text/plain')

      document.execCommand('insertText', false, text)

      this.updateTemplateBody()
    },

    templateInsertLink() {
      this.focusTemplateEditor()
      const url = prompt('Enter link URL')
      if (!url) return

      document.execCommand('createLink', false, url)
      this.updateTemplateBody()
    },

    templateInsertImage() {
      this.focusTemplateEditor()
      const url = prompt('Enter image URL')
      if (!url) return

      document.execCommand('insertImage', false, url)
      this.updateTemplateBody()
    },

    templateInsertCode() {
      this.focusTemplateEditor()

      document.execCommand(
        'insertHTML',
        false,
        '<pre><code>Code here</code></pre>'
      )

      this.updateTemplateBody()
    },

    async loadInitialData() {
      this.loading = true
      try {
        await this.loadTemplates()
        await this.loadEmailLogs()
        this.applyEmailFilter()
      } catch (error) {
        console.error('Load initial data error:', error)
        this.showToast('error', 'Failed to load data: ' + error.message)
      } finally {
        this.loading = false
      }
    },

    async loadUserWorkingGroups() {
      this.loadingWorkingGroups = true;

      try {
        const response = await WorkingGroupServices.getWorkingGroups();

        console.log("RAW WORKING GROUP RESPONSE:", response);

        const groups =
          response?.Data?.data ||      // YOUR backend wraps data here
          response?.data?.data ||      // fallback
          [];

        console.log("PARSED GROUPS:", groups);

        this.userWorkingGroups = groups;
      } catch (error) {
        console.error("ERROR LOADING WORKING GROUPS:", error);
        this.showToast("error", "Failed to load working groups");
        this.userWorkingGroups = [];
      } finally {
        this.loadingWorkingGroups = false;
      }
    },

    async loadWorkingGroupEmails() {
      if (!this.composeEmail.workingGroupId) {
        this.composeEmail.addresses = ''
        return
      }

      try {
        const response = await WorkingGroupServices.getWorkingGroupMembers(
          this.composeEmail.workingGroupId
        )

        // Again defensive in case backend wraps data
        const members =
          response?.Data?.data ||
          response?.data?.data ||
          response?.data ||
          []

        const emails = Array.from(
          new Set(
            members
              .map(m => m.email || m.user?.email)
              .filter(Boolean)
          )
        )

        this.composeEmail.addresses = emails.join(', ')

        this.showToast(
          'success',
          `Loaded ${emails.length} member(s) from the selected working group`
        )
      } catch (error) {
        console.error('Load working group emails error:', error)
        this.showToast('error', 'Failed to load working group member emails')
        this.composeEmail.addresses = ''
      }
    },

    async loadEdctpCallEmails() {
      if (!this.composeEmail.edctpCall) {
        this.composeEmail.addresses = ''
        return
      }

      try {
        // Get ALL users (backend isn't filtering by edctp_project_call for us)
        const response = await UserService.getUsers({ per_page: 1000 })

        const users = response?.Data?.data || response?.data?.data || []

        const selectedKey = String(this.composeEmail.edctpCall) // e.g. "1"
        const selectedLabel = this.edctpCallMap[selectedKey]     // e.g. "Career Development Fellowship (CDF)"

        // Try to extract abbreviation from label, e.g. "(CDF)" → "CDF"
        let selectedAbbrev = null
        if (selectedLabel) {
          const match = selectedLabel.match(/\(([^)]+)\)/) // text in parentheses
          if (match && match[1]) {
            selectedAbbrev = match[1].trim()
          }
        }

        // Filter: must be Fellow + have matching edctp_project_call in fellowship_details
        const fellowUsers = users.filter(user => {
          const roles = user.roles || []
          const isFellow = roles.some(r => (r?.name || '').toLowerCase() === 'fellow')
          if (!isFellow) return false

          const details = user.fellowship_details || []
          if (!details.length) return false

          return details.some(fd => {
            const raw = fd.edctp_project_call
            if (!raw) return false
            const value = raw.toString().trim()

            // Match numeric code, full label, or abbreviation
            if (value === selectedKey) return true
            if (selectedLabel && value === selectedLabel) return true
            if (selectedAbbrev && value.toLowerCase() === selectedAbbrev.toLowerCase()) return true

            return false
          })
        })

        // Build unique email list
        const emailSet = new Set(
          fellowUsers
            .filter(u => u.email)
            .map(u => u.email)
        )

        const emails = Array.from(emailSet)

        this.composeEmail.addresses = emails.join(', ')

        this.showToast(
          'success',
          `Loaded ${emails.length} Fellow(s) for the selected EDCTP call group`
        )
      } catch (error) {
        console.error('Load EDCTP call emails error:', error)
        this.showToast('error', 'Failed to load EDCTP call group emails')
        this.composeEmail.addresses = ''
      }
    },

    async setActiveTab(tab) {
      this.activeTab = tab
      if (tab === 'view') {
        await this.loadEmailLogs()
        this.applyEmailFilter()
      }
    },

    // NEW: Email viewing methods
    async refreshEmailList() {
      await this.loadEmailLogs()
      this.applyEmailFilter()
      this.showToast('success','Email list refreshed')
    },

    setEmailFilter(filter) {
      this.emailFilter = filter
      this.applyEmailFilter()
      this.currentEmailPage = 1
    },

    applyEmailFilter() {
      let filtered = [...this.sentEmails]

      // Apply status filter
      if (this.emailFilter !== 'all') {
        filtered = filtered.filter(email => email.status === this.emailFilter)
      }

      // Apply search filter
      if (this.emailSearchQuery.trim()) {
        const query = this.emailSearchQuery.toLowerCase()
        filtered = filtered.filter(email =>
          email.recipient.toLowerCase().includes(query) ||
          email.subject.toLowerCase().includes(query) ||
          (email.content && email.content.toLowerCase().includes(query))
        )
      }

      this.filteredEmails = filtered
    },

    searchEmails() {
      this.applyEmailFilter()
      this.currentEmailPage = 1
    },

    clearSearch() {
      this.emailSearchQuery = ''
      this.applyEmailFilter()
      this.currentEmailPage = 1
    },

    setEmailPage(page) {
      if (page >= 1 && page <= this.totalEmailPages) {
        this.currentEmailPage = page
      }
    },

    getEmailCountByStatus(status) {
      return this.sentEmails.filter(email => email.status === status).length
    },

    viewEmailDetails(email) {
      this.selectedEmailDetails = email
      this.emailViewMode = 'rendered'
      this.showEmailDetailsDialog = true
    },

    getStatusIcon(status) {
      switch (status) {
        case 'sent':
          return 'bi-check-circle'
        case 'pending':
          return 'bi-clock'
        case 'failed':
          return 'bi-exclamation-circle'
        default:
          return 'bi-question-circle'
      }
    },

    formatDetailedDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit'
      })
    },

    async copyEmailContent() {
      if (this.selectedEmailDetails) {
        try {
          await navigator.clipboard.writeText(this.selectedEmailDetails.content)
          this.showToast('success','Email content copied to clipboard')
        } catch (error) {
          console.error('Copy failed:', error)
          this.showToast('error', 'Failed to copy content')
        }
      }
    },

    async resendEmail(email) {
      if (confirm('Are you sure you want to retry sending this email?')) {
        try {
          await EmailService.resendEmail(email.id)
          this.showToast('success','Email queued for resending')
          await this.refreshEmailList()
          this.showEmailDetailsDialog = false
        } catch (error) {
          console.error('Resend email error:', error)
          this.showToast('error', 'Failed to resend email')
        }
      }
    },

    async loadAllUsers() {
      this.loadingUsers = true
      try {
        const response = await UserService.getUsers({ per_page: 1000 })
        let usersData = []
        if (response.Data.data) {
          usersData = response.Data.data
        } else if (response && response.data) {
          usersData = Array.isArray(response.data) ? response.data : []
        } else {
          usersData = []
        }
        this.allUsers = usersData.filter(user => user.email)
      } catch (error) {
        console.error('Load users error:', error)
        this.showToast('error', 'Failed to load users: ' + error.message)
        this.allUsers = []
      } finally {
        this.loadingUsers = false
      }
    },

    async showUserSelection() {
      if (this.allUsers.length === 0) {
        await this.loadAllUsers()
      }
      this.selectedUsers = []
      this.showUserListDialog = true
    },

    async addAllUserEmails() {
      if (this.allUsers.length === 0) {
        await this.loadAllUsers()
      }
      if (this.allUsers.length > 0) {
        const emailAddresses = this.allUsers.map(user => user.email).join(', ')
        this.composeEmail.addresses = emailAddresses
        this.showToast('success',`Added ${this.allUsers.length} user email addresses`)
      } else {
        this.toast.warning('No users found in the system')
      }
    },

    addSelectedUserEmails() {
      if (this.selectedUsers.length === 0) {
        this.toast.warning('Please select at least one user')
        return
      }
      const selectedEmails = this.selectedUsers.map(userId => {
        const user = this.allUsers.find(u => u.id === userId)
        return user ? user.email : null
      }).filter(email => email)

      const existingAddresses = this.composeEmail.addresses
        ? this.composeEmail.addresses.split(',').map(email => email.trim()).filter(email => email)
        : []

      const allEmails = [...new Set([...existingAddresses, ...selectedEmails])]
      this.composeEmail.addresses = allEmails.join(', ')
      this.showUserListDialog = false
      this.selectedUsers = []
      this.showToast('success',`Added ${selectedEmails.length} user email addresses`)
    },

    toggleUserSelection(userId) {
      const index = this.selectedUsers.indexOf(userId)
      if (index > -1) {
        this.selectedUsers.splice(index, 1)
      } else {
        this.selectedUsers.push(userId)
      }
    },

    isUserSelected(userId) {
      return this.selectedUsers.includes(userId)
    },

    selectAllUsers() {
      if (this.selectedUsers.length === this.allUsers.length) {
        this.selectedUsers = []
      } else {
        this.selectedUsers = this.allUsers.map(user => user.id)
      }
    },

    getUserDisplayName(user) {
      if (user.first_name && user.last_name) {
        return `${user.first_name} ${user.last_name}`
      } else if (user.name) {
        return user.name
      } else {
        return user.email
      }
    },

    async loadTemplates() {
      try {
        const response = await EmailService.getEmailTemplates({
          per_page: 100
        })
        let templatesData = []

        if (response && response.Data && response.Data.data && response.Data.data.data) {
          templatesData = response.Data.data.data
        } else if (response && response.Data && response.Data.data) {
          templatesData = response.Data.data
        } else {
          console.warn('Unexpected response structure:', response)
          templatesData = []
        }

        this.automatedTemplates = templatesData.filter(template => template.type === 'automated')
        this.customTemplates = templatesData.filter(template => template.type === 'custom')
      } catch (error) {
        console.error('Load templates error:', error)
        this.showToast('error', 'Failed to load templates')
        this.automatedTemplates = []
        this.customTemplates = []
      }
    },

    async loadEmailLogs() {
      try {
        const response = await EmailService.getEmailLogs({ per_page: 5000000 })
        console.log('Log Response: ', response)

        // your Axios wrapper is probably returning { Data: { success, data } }
        const api = response.Data

        if (api?.success) {
          this.sentEmails = api.data?.data || []
        } else {
          this.sentEmails = []
        }

      } catch (error) {
        console.error('Load email logs error:', error)
        this.showToast('error', 'Failed to load email logs')
        this.sentEmails = []
      }
    },

    async viewTemplate(template) {
      // Show the dialog immediately with the template data we already have
      this.templateViewMode = 'rendered'
      this.viewingTemplate = {
        id: template.id || 'N/A',
        name: template.name || 'Unknown Template',
        subject: template.subject || 'No subject',
        body: template.body || 'No content available',
        type: template.type || 'custom',
        description: template.description || '',
        created_at: template.created_at || null,
        updated_at: template.updated_at || null
      }
      this.previewedTemplateSubject = this.viewingTemplate.subject
      this.previewedTemplateBody = this.viewingTemplate.body
      this.templatePreviewVariablesUsed = this.getTemplateVariables(this.viewingTemplate.subject + ' ' + this.viewingTemplate.body)
      this.templatePreviewVariablesText = '{}'
      this.templateTestEmail = ''
      this.showViewDialog = true
      await this.loadTemplatePreviewFromInput()

    },

    editTemplate(template) {
      this.editingTemplate = template
      this.currentTemplateType = template.type
      this.templateForm = {
        name: template.name || '',
        subject: template.subject || '',
        body: template.body || '',
        type: template.type || 'custom'
      }
      this.formErrors = {}
      this.showTemplateDialog = true
      this.$nextTick(() => {
        if (this.$refs.templateEditor) {
          this.$refs.templateEditor.innerHTML = this.templateForm.body || ''
        }
      })
    },

    showNewTemplateDialog(type) {
      this.currentTemplateType = type || 'custom'
      this.editingTemplate = null
      this.templateForm = {
        name: '',
        subject: '',
        body: '',
        type: type || 'custom'
      }
      this.formErrors = {}
      this.showTemplateDialog = true
      this.$nextTick(() => {
        if (this.$refs.templateEditor) {
          this.$refs.templateEditor.innerHTML = ''
        }
      })
    },

    async deleteTemplate(template) {
      if (confirm('Are you sure you want to delete this template?')) {
        try {
          const response = await EmailService.deleteEmailTemplate(template.id)
          if (response.success) {
            this.showToast('success','Template deleted successfully!')
          } else {
            this.showToast('success','Template deleted successfully!')
          }
          await this.loadTemplates()
        } catch (error) {
          this.showToast('error', 'Failed to delete template')
          console.error('Delete template error:', error)
        }
      }
    },

    async saveTemplate() {
      this.formErrors = {}
      const errors = {}

      if (!this.templateForm.name || !this.templateForm.name.trim()) {
        errors.name = 'Template name is required'
      }
      if (!this.templateForm.subject || !this.templateForm.subject.trim()) {
        errors.subject = 'Subject is required'
      }
      if (!this.templateForm.body || !this.templateForm.body.trim()) {
        errors.body = 'Content is required'
      }

      if (Object.keys(errors).length > 0) {
        this.formErrors = errors
        this.showToast('error', 'Please fill in all required fields')
        return
      }

      this.savingTemplate = true
      try {
        let response

        if (this.editingTemplate) {
          const updateData = {
            name: this.templateForm.name.trim(),
            subject: this.templateForm.subject.trim(),
            body: this.templateForm.body.trim(),
            type: this.templateForm.type
          }
          response = await EmailService.updateEmailTemplate(this.editingTemplate.id, updateData)
        } else {
          const templateData = {
            name: this.templateForm.name.trim(),
            subject: this.templateForm.subject.trim(),
            body: this.templateForm.body.trim(),
            type: this.templateForm.type
          }
          response = await EmailService.createEmailTemplate(templateData)
        }

        if (response && (response.Success === true || response.success === true)) {
          this.showToast('success',this.editingTemplate ? 'Template updated successfully!' : 'Template created successfully!')
        } else {
          this.showToast('success',this.editingTemplate ? 'Template updated successfully!' : 'Template created successfully!')
        }

        this.showTemplateDialog = false
        this.editingTemplate = null
        this.templateForm = {
          name: '',
          subject: '',
          body: '',
          type: 'custom'
        }
        this.formErrors = {}
        await this.loadTemplates()
      } catch (error) {
        console.error('Save template error:', error)
        if (error.response && error.response.data && error.response.data.errors) {
          this.formErrors = error.response.data.errors
          this.showToast('error', error.response.data.message || 'Validation failed')
        } else {
          this.showToast('error', this.editingTemplate ? 'Failed to update template' : 'Failed to create template')
        }
      } finally {
        this.savingTemplate = false
      }
    },

    async loadTemplate() {
      if (!this.selectedTemplate) {
        this.composeEmail.subject = ''
        this.composeEmail.body = ''

        // Clear editor UI
        this.$nextTick(() => {
          if (this.$refs.emailEditor) {
            this.$refs.emailEditor.innerHTML = ''
          }
        })

        return
      }

      try {
        let template =
          this.customTemplates.find(t => t.id === this.selectedTemplate) ||
          this.automatedTemplates.find(t => t.id === this.selectedTemplate)

        if (!template) {
          this.showToast('error', 'Template not found')
          return
        }

        // 1️⃣ Update reactive data
        this.composeEmail.subject = template.subject || ''
        this.composeEmail.body = template.body || ''
      } catch (error) {
        console.error('Load template error:', error)
        this.showToast('error', 'Failed to load template')
      }

      // 2️⃣ Sync WYSIWYG editor AFTER DOM update
      this.$nextTick(() => {
        if (this.$refs.emailEditor) {
          this.$refs.emailEditor.innerHTML = this.composeEmail.body || ''
        }
      })
    },

    async sendEmail() {
      if (
        !this.composeEmail.addresses.trim() ||
        !this.composeEmail.subject.trim() ||
        !this.composeEmail.body.trim()
      ) {
        this.showToast('error', 'Please fill in all required fields')
        return
      }

      this.sendingEmail = true

      try {
        const recipients = this.composeEmail.addresses
          .split(',')
          .map(e => e.trim())
          .filter(Boolean)

        if (!recipients.length) {
          this.showToast('error', 'Please add at least one valid email address')
          return
        }

        // TEMPLATE SEND
        if (this.selectedTemplate) {
          for (const email of recipients) {
            await EmailService.sendEmailWithTemplate(this.selectedTemplate, {
              recipient: email,
              variables: {}
            })
          }
        }
        // CUSTOM SEND (NO TEMPLATE)
        else {
          await EmailService.sendCustomEmail({
            recipients: recipients.join(','), // ✅ REQUIRED BY BACKEND
            subject: this.composeEmail.subject,
            body: this.composeEmail.body
          })
        }

        this.showToast('success', 'Email(s) sent successfully!')
        this.clearForm()

      } catch (error) {
        console.error(error)
        this.showToast('error', 'Failed to send email')
      } finally {
        this.sendingEmail = false
      }
    },

    clearForm() {
      this.composeEmail = {
        addresses: '',
        sendType: this.composeEmail.sendType, // preserve selected send type
        subject: '',
        body: '',
        attachments: [],
        edctpCall: '',
        workingGroupId: ''
      }

      this.selectedTemplate = ''

      // Reset WYSIWYG editor UI AFTER DOM update
      this.$nextTick(() => {
        if (this.$refs.emailEditor) {
          this.$refs.emailEditor.innerHTML = ''
        }
      })
    },

    emailInsertCode() {
      this.focusEmailEditor()

      document.execCommand(
        'insertHTML',
        false,
        '<pre><code>Code here</code></pre>'
      )

      this.updateEmailBody()
    },

    viewEmailContent(email) {
      this.viewingEmailContent = email
      this.showEmailContentDialog = true
    },

    getStatusClass(status) {
      switch (status) {
        case 'sent':
          return 'bg-success'
        case 'pending':
          return 'bg-warning'
        case 'failed':
          return 'bg-danger'
        default:
          return 'bg-secondary'
      }
    },

    getContentPreview(content) {
      if (!content) return '-'
      const textContent = content.replace(/<[^>]*>/g, '')
      return textContent.length > 100 ? textContent.substring(0, 100) + '...' : textContent
    },

    formatDate(dateString) {
      if (!dateString) return '-'
      const date = new Date(dateString)
      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    },

    getTemplateTypeBadgeClass() {
      const template = this.customTemplates.find(t => t.id === this.selectedTemplate) ||
        this.automatedTemplates.find(t => t.id === this.selectedTemplate)
      if (template?.type === 'automated') {
        return 'bg-info text-dark'
      } else if (template?.type === 'custom') {
        return 'bg-success text-white'
      }
      return 'bg-secondary'
    },

    async handleSendTypeChange() {
      if (this.composeEmail.sendType === 'all') {
        await this.loadAllUsersForSendType()
      } else if (this.composeEmail.sendType === 'edctp_group') {
        // Reset call group selection & addresses
        this.composeEmail.edctpCall = ''
        this.composeEmail.addresses = ''
      } else if (this.composeEmail.sendType === 'working_group') {
        // Reset working group selection & addresses and load groups user belongs to
        this.composeEmail.workingGroupId = ''
        this.composeEmail.addresses = ''
        await this.loadUserWorkingGroups()
      } else if (this.composeEmail.sendType === 'specific') {
        // Specific – no auto population by default
        this.composeEmail.addresses = ''
      }
    },

    async loadAllUsersForSendType() {
      if (this.allUsers.length === 0) {
        await this.loadAllUsers()
      }
      if (this.allUsers.length > 0) {
        const emailAddresses = this.allUsers.map(user => user.email).join(', ')
        this.composeEmail.addresses = emailAddresses
        this.showToast('success',`Automatically populated with ${this.allUsers.length} user email addresses`)
      } else {
        this.toast.warning('No users found in the system')
        this.composeEmail.sendType = 'specific'
      }
    },

    handleFileUpload(event) {
      this.composeEmail.attachments = Array.from(event.target.files)
    },

    // NEW: Template viewing helper methods
    getTemplateVariables(content) {
      if (!content) return []
      const matches = content.match(/\{\{([^}]+)\}\}/g)
      if (!matches) return []
      return [...new Set(matches.map(match => match.replace(/[{}]/g, '').trim()))]
    },

    parseTemplatePreviewVariables() {
      try {
        const parsed = JSON.parse(this.templatePreviewVariablesText || '{}')
        return parsed && typeof parsed === 'object' ? parsed : {}
      } catch (error) {
        throw new Error('Invalid JSON format for preview variables')
      }
    },

    async loadTemplatePreviewFromInput() {
      if (!this.viewingTemplate?.id) {
        return
      }

      this.templatePreviewLoading = true
      try {
        const variables = this.parseTemplatePreviewVariables()
        const response = await EmailService.previewEmailTemplate(this.viewingTemplate.id, variables)
        const api = response.Data

        if (api?.success) {
          const preview = api.data?.preview || {}
          this.previewedTemplateSubject = preview.subject || this.viewingTemplate.subject
          this.previewedTemplateBody = preview.body || this.viewingTemplate.body
          this.templatePreviewVariablesUsed = api.data?.variables_used || this.templatePreviewVariablesUsed
        } else {
          this.showToast('error', 'Failed to load template preview')
        }
      } catch (error) {
        this.showToast('error', error.message || 'Failed to load template preview')
      } finally {
        this.templatePreviewLoading = false
      }
    },

    async copyTemplateContent() {
      const html = this.previewedTemplateBody || this.viewingTemplate?.body || ''
      if (html) {
        try {
          await navigator.clipboard.writeText(html)
          this.showToast('success','Template content copied to clipboard')
        } catch (error) {
          console.error('Copy failed:', error)
          this.showToast('error', 'Failed to copy content')
        }
      }
    },

    async sendTemplateTest() {
      if (!this.viewingTemplate?.id) {
        return
      }

      if (!this.templateTestEmail || !this.templateTestEmail.trim()) {
        this.showToast('error', 'Please enter a test email address')
        return
      }

      this.templateTestSending = true
      try {
        const variables = this.parseTemplatePreviewVariables()
        const response = await EmailService.testTemplate(this.viewingTemplate.id, {
          test_email: this.templateTestEmail.trim(),
          variables
        })
        const api = response.Data

        if (api?.success) {
          this.showToast('success', 'Test email sent successfully')
          await this.loadEmailLogs()
        } else {
          this.showToast('error', api?.message || 'Failed to send test email')
        }
      } catch (error) {
        console.error('Send test email error:', error)
        this.showToast('error', 'Failed to send test email')
      } finally {
        this.templateTestSending = false
      }
    }
  }
}
</script>

<style scoped>
.email-admin-page {
  padding: 1rem 0;
}

.modal.show {
  background-color: rgba(0, 0, 0, 0.5);
}

.nav-tabs .nav-link {
  cursor: pointer;
  color: #6c757d;
}

.nav-tabs .nav-link.active {
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  color: #17a2b8;
  font-weight: 600;
}

.nav-tabs .nav-link:hover {
  color: #17a2b8;
}

.tab-content {
  min-height: 400px;
}

/* Template specific styles */
.template-links {
  max-width: 100%;
}

.template-item {
  border: 1px solid #e9ecef;
  border-radius: 8px;
  padding: 1rem;
  background-color: #fff;
  transition: all 0.3s ease;
}

.template-item:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transform: translateY(-2px);
}

.template-link {
  color: #17a2b8;
  text-decoration: none;
  font-weight: 500;
}

.template-link:hover {
  color: #138496;
  text-decoration: underline;
}

.template-content {
  margin-top: 1rem;
}

.template-preview {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
}

.template-content-preview {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  max-height: 300px;
  overflow-y: auto;
}

.email-content-preview {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 4px;
  font-size: 0.9rem;
  line-height: 1.5;
  white-space: pre-wrap;
  max-height: 400px;
  overflow-y: auto;
}

.content-preview {
  max-width: 300px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.content-preview:hover {
  background-color: #f8f9fa;
  border-radius: 4px;
  padding: 2px 4px;
}

/* NEW: Email view styles */
.stats-card-email {
  display: flex;
  align-items: center;
  padding: 1rem;
  background: white;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  margin-bottom: 1rem;
}

.stats-icon {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 1rem;
}

.stats-icon i {
  font-size: 1.5rem;
  color: white;
}

.stats-content h6 {
  margin: 0;
  font-size: 0.875rem;
  color: #6c757d;
  text-transform: uppercase;
  font-weight: 600;
}

.stats-content h4 {
  margin: 0;
  font-size: 1.75rem;
  font-weight: 700;
  color: #2c3e50;
}

.email-list {
  background: white;
  border-radius: 8px;
}

.email-item {
  padding: 1.25rem;
  border-bottom: 1px solid #e9ecef;
  cursor: pointer;
  transition: all 0.2s ease;
}

.email-item:last-child {
  border-bottom: none;
}

.email-item:hover {
  background-color: #f8f9fa;
  transform: translateX(4px);
}

.email-editor-content {
  min-height: 200px;          /* 👈 make it taller */
  padding: 12px;
  resize: vertical;           /* optional: allow manual resize */
  line-height: 1.6;
}


.email-header {
  display: flex;
  justify-content: between;
  align-items: center;
  margin-bottom: 0.75rem;
}

.email-recipient {
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

.email-date {
  font-size: 0.875rem;
  color: #6c757d;
  display: flex;
  align-items: center;
}

.email-subject {
  font-size: 1rem;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
}

.email-content-preview {
  color: #6c757d;
  font-size: 0.875rem;
  line-height: 1.4;
  margin-bottom: 0.5rem;
}

.email-footer {
  display: flex;
  justify-content: between;
  align-items: center;
  font-size: 0.8rem;
}

.email-type {
  display: flex;
  align-items: center;
}

.email-error {
  display: flex;
  align-items: center;
}

/* NEW: Email details modal styles */
.modal-xl {
  max-width: 1200px;
}

.email-details-header {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
  margin-bottom: 1rem;
}

.email-meta-info {
  background-color: #fff;
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.meta-item {
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
}

.meta-item:last-child {
  margin-bottom: 0;
}

.email-content-section {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.rendered-email-container {
  background-color: #fff;
  padding: 2rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  min-height: 200px;
  max-height: 500px;
  overflow-y: auto;
}

.email-source-content {
  background-color: #f8f9fa;
}

.source-code {
  background-color: #2d3748;
  color: #e2e8f0;
  padding: 1.5rem;
  margin: 0;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  max-height: 500px;
  overflow-y: auto;
  white-space: pre-wrap;
  word-wrap: break-word;
}

.email-actions {
  border-top: 1px solid #dee2e6;
  padding-top: 1rem;
}

.email-id-info {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace;
}

/* Rich editor styles */
.rich-editor-toolbar {
  border: 1px solid #ced4da;
  border-bottom: none;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-radius: 0.375rem 0.375rem 0 0;
}

.rich-editor {
  border-radius: 0 0 0.375rem 0.375rem;
}

/* Dropdown styles */
.dropdown-toggle::after {
  display: none;
}

.dropdown-item {
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.dropdown-item.text-danger:hover {
  background-color: #f8d7da;
  color: #721c24;
}

.dropdown-menu {
  border: none;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

/* Modal styles */
.modal-content {
  border-radius: 8px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.modal-header {
  border-bottom: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
  background-color: #f8f9fa;
}

.modal-title {
  color: #2c3e50;
  font-weight: 600;
}

.modal-body {
  padding: 1.5rem;
}

.modal-footer {
  border-top: 1px solid #e9ecef;
  padding: 1rem 1.5rem;
}

.btn-close:hover {
  opacity: 1;
}

/* Badge styles */
.badge {
  font-size: 0.75rem;
  padding: 0.375rem 0.75rem;
}

/* Form styles */
.form-check {
  margin-bottom: 0.5rem;
}

.form-check-input {
  margin-top: 0.25rem;
}

.form-check-label {
  margin-left: 0.5rem;
}

/* Table styles */
.table th {
  background-color: #f8f9fa;
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.875rem;
  color: #6c757d;
}

.table td {
  vertical-align: middle;
}

.table-hover tbody tr:hover {
  background-color: #f8f9fa;
}

/* Card styles */
.card {
  border: none;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #dee2e6;
}

/* Button styles */
.btn-primary {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

.btn-primary:hover {
  background-color: #138496;
  border-color: #117a8b;
}

.btn {
  transition: all 0.2s ease;
}

/* Pagination styles */
.pagination {
  margin: 0;
}

.page-link {
  color: #17a2b8;
  border-color: #dee2e6;
}

.page-link:hover {
  color: #138496;
  background-color: #e9ecef;
  border-color: #dee2e6;
}

.page-item.active .page-link {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

/* User selection styles */
.user-list-container {
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
}

.list-group-item {
  border: none;
  border-bottom: 1px solid #dee2e6;
  transition: all 0.2s ease;
}

.list-group-item:last-child {
  border-bottom: none;
}

.list-group-item:hover {
  background-color: #f8f9fa;
}

.list-group-item.active {
  background-color: #e3f2fd;
  border-color: #2196f3;
}

.list-group-item.active h6 {
  color: #1976d2;
}

/* Input group enhancements */
.input-group-append {
  margin-left: 0.5rem;
}

.btn-group-vertical .btn {
  border-radius: 0.375rem;
  margin-bottom: 0.25rem;
}

.btn-group-vertical .btn:last-child {
  margin-bottom: 0;
}

/* Enhanced button styles */
.btn-outline-primary:hover {
  background-color: #17a2b8;
  border-color: #17a2b8;
  color: white;
}

.btn-outline-secondary:hover {
  background-color: #6c757d;
  border-color: #6c757d;
  color: white;
}

/* User list item enhancements */
.list-group-item h6 {
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.list-group-item p {
  font-size: 0.875rem;
  margin-bottom: 0.25rem;
}

.list-group-item small {
  font-size: 0.75rem;
}

/* Checkbox styling */
.form-check-input {
  margin-top: 0.125rem;
}

/* Loading states */
.spinner-border-sm {
  width: 1rem;
  height: 1rem;
}

/* Badge enhancements */
.badge {
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
}

/* Modal enhancements for user selection */
.modal-lg {
  max-width: 800px;
}

/* Disabled textarea styling */
.form-control:disabled {
  background-color: #f8f9fa;
  border-color: #dee2e6;
  color: #6c757d;
  opacity: 0.8;
}

.form-control:disabled::placeholder {
  color: #6c757d;
  font-style: italic;
}

/* Send type radio buttons */
.form-check {
  margin-bottom: 0.75rem;
}

.form-check-label {
  font-weight: 500;
  cursor: pointer;
}

.form-check-label small {
  font-weight: 400;
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.25rem;
}

.form-check-input:checked {
  background-color: #17a2b8;
  border-color: #17a2b8;
}

/* Badge styling for auto-selection indicator */
.badge.bg-info {
  background-color: #d1ecf1 !important;
  color: #0c5460 !important;
  border: 1px solid #b8daff;
}

/* Auto-selection visual feedback */
.bg-light {
  background-color: #f8f9fa !important;
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 10px,
    rgba(255,255,255,.5) 10px,
    rgba(255,255,255,.5) 20px
  );
}

/* Template view styles */
.template-view-content {
  min-height: 300px;
}

.template-info {
  background-color: #f8f9fa;
  padding: 1.5rem;
  border-radius: 8px;
}

.info-item {
  margin-bottom: 1rem;
}

.info-item:last-child {
  margin-bottom: 0;
}

.info-label {
  font-weight: 600;
  color: #495057;
  display: flex;
  align-items: center;
  margin-bottom: 0.5rem;
  font-size: 0.9rem;
}

.info-value {
  color: #212529;
  background-color: white;
  padding: 0.75rem;
  border-radius: 4px;
  border: 1px solid #dee2e6;
  font-size: 0.95rem;
}

.template-meta {
  height: 100%;
}

.meta-card {
  background-color: white;
  border: 1px solid #dee2e6;
  border-radius: 8px;
  padding: 1.25rem;
  height: 100%;
}

.meta-item {
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #f1f3f4;
}

.meta-item:last-child {
  margin-bottom: 0;
  padding-bottom: 0;
  border-bottom: none;
}

.template-content-section {
  border: 1px solid #dee2e6;
  border-radius: 8px;
  overflow: hidden;
}

.template-rendered-preview {
  background-color: #fff;
}

.preview-container {
  padding: 2rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  min-height: 200px;
  max-height: 400px;
  overflow-y: auto;
  background-color: #fff;
}

.template-source-preview {
  background-color: #f8f9fa;
}

.template-variables {
  background-color: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #dee2e6;
}

.variables-list {
  margin: 0.5rem 0;
}

.font-monospace {
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', monospace !important;
  font-size: 0.85rem;
}
@media (max-width: 768px) {
  .template-item {
    padding: 0.75rem;
  }

  .template-preview {
    padding: 0.75rem;
  }

  .modal-dialog {
    margin: 1rem;
  }

  .rich-editor-toolbar {
    padding: 0.25rem;
  }

  .rich-editor-toolbar .btn {
    padding: 0.25rem 0.5rem;
    margin: 0.125rem;
  }

  .input-group-append {
    margin-left: 0;
    margin-top: 0.5rem;
  }

  .btn-group-vertical {
    flex-direction: row;
  }

  .btn-group-vertical .btn {
    margin-bottom: 0;
    margin-right: 0.25rem;
    flex: 1;
  }

  .btn-group-vertical .btn:last-child {
    margin-right: 0;
  }

  .modal-lg {
    max-width: 95%;
    margin: 0.5rem;
  }

  .user-list-container {
    max-height: 300px;
  }

  .email-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .stats-card-email {
    margin-bottom: 0.5rem;
  }

  .email-footer {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }

  .modal-xl {
    max-width: 95%;
    margin: 0.5rem;
  }
}

/* --- CLEAN, MODERN EMAIL HISTORY UI --- */

/* Email list wrapper */
.email-list {
  background: #ffffff;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #eceff3;
}

/* Each email card row */
.email-item {
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #eef2f5;
  cursor: pointer;
  transition: all .18s ease;
}

.email-item:last-child {
  border-bottom: none;
}

.email-item:hover {
  background-color: #f8fafc;
  transform: translateX(4px);
}

/* Header: recipient + status badge + date */
.email-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: .5rem;
}

/* Recipient */
.email-recipient {
  display: flex;
  align-items: center;
  font-size: .95rem;
  font-weight: 600;
  color: #1a202c;
}

.email-recipient i {
  font-size: 1.1rem;
  color: #4a5568;
  margin-right: 6px;
}

/* Date */
.email-date {
  font-size: .85rem;
  color: #718096;
  display: flex;
  align-items: center;
}

.email-date i {
  margin-right: 4px;
}

/* Subject line */
.email-subject {
  margin-bottom: .35rem;
  font-size: 1.05rem;
  font-weight: 600;
  color: #2d3748;
  display: flex;
  align-items: center;
}

.email-subject i {
  margin-right: 6px;
  color: #17a2b8;
}

/* Content preview block */
.email-content-preview {
  color: #4a5568;
  font-size: .9rem;
  line-height: 1.45;
  max-height: 3.8rem;          /* keeps text tidy */
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 3;       /* 3-line preview */
  -webkit-box-orient: vertical;
  margin-bottom: .5rem;
}

/* Footer row: type + error */
.email-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: .4rem;
  font-size: .8rem;
}

/* Type badge */
.email-type .badge {
  padding: .3rem .55rem;
  border-radius: 12px;
  font-size: .75rem;
}

/* Status badges (cleaner & consistent) */
.badge.bg-success,
.badge.bg-warning,
.badge.bg-danger,
.badge.bg-secondary {
  padding: .35rem .65rem;
  border-radius: 12px;
  font-size: .75rem;
  text-transform: capitalize;
}

/* Error message text */
.email-error small {
  color: #e53e3e;
  font-weight: 500;
  display: flex;
  align-items: center;
}

.email-error i {
  margin-right: 4px;
}

/* Rich Text Editor Styles */
.rich-text-editor {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
}

.rich-text-editor.is-invalid {
  border-color: #dc2626;
}

.editor-toolbar {
  background: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.5rem;
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  align-items: center;
}

.toolbar-group {
  display: flex;
  gap: 0.125rem;
}

.toolbar-divider {
  width: 1px;
  height: 24px;
  background: #dee2e6;
  margin: 0 0.25rem;
}

.toolbar-btn {
  background: none;
  border: 1px solid transparent;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.875rem;
  color: #495057;
  transition: all 0.15s ease;
  min-width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.toolbar-btn:hover {
  background: #e9ecef;
  border-color: #ced4da;
}

.toolbar-btn:active {
  background: #dee2e6;
}

.toolbar-select {
  background: white;
  border: 1px solid #ced4da;
  padding: 0.375rem 0.5rem;
  border-radius: 4px;
  font-size: 0.875rem;
  min-width: 100px;
}

.color-btn {
  position: relative;
}

.color-indicator {
  display: block;
  width: 16px;
  height: 16px;
  border-radius: 2px;
  border: 1px solid #ccc;
}

.editor-content {
  min-height: 200px;
  padding: 1rem;
  background: white;
  outline: none;
  line-height: 1.6;
  font-family: inherit;
  font-size: 1rem;
  color: #333;
}

.editor-content:empty:before {
  content: attr(placeholder);
  color: #999;
  font-style: italic;
}

.editor-content:focus {
  background: #fefefe;
}

</style>
